import { useState } from "react";
import { toast } from "sonner";
import { Bell, Cctv, Camera, Lock, Thermometer, DoorOpen, Send, CheckCircle2, Smartphone, ShieldCheck, Zap, Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const ORANGE = "#FF7A00";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/50 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#FF7A00] focus:outline-none transition-colors duration-300";

const EQUIPMENT = [
  { icon: Bell, name: "Doorbell Camera Pro", desc: "See, hear, and speak to anyone at your door — with package detection that actively deters porch pirates." },
  { icon: Cctv, name: "Outdoor Camera Pro", desc: "Smart Sentry spots lurkers and warns them off with lights and sound before anything happens." },
  { icon: Camera, name: "Indoor Camera Pro", desc: "Two-way talk and one-touch callout so your kids can reach you the moment they're home." },
  { icon: Lock, name: "Smart Lock", desc: "Lock and unlock from anywhere, assign codes to family and guests, and never hide a key again." },
  { icon: Thermometer, name: "Smart Thermostat", desc: "Learns your schedule and trims energy waste automatically — comfort when home, savings when away." },
  { icon: DoorOpen, name: "Entry, Motion & Glass-Break Sensors", desc: "Every door, window, and room covered — instantly relayed to 24/7 monitoring the second something's wrong." },
];

const PACKAGES = [
  {
    name: "Smart Security",
    price: "$29.99",
    tag: "Essential protection",
    features: ["24/7 professional monitoring", "Entry, motion & smoke coverage", "Vivint app control", "Touchscreen Smart Hub panel"],
  },
  {
    name: "Smart Home",
    price: "$39.99",
    tag: "Most popular",
    featured: true,
    features: ["Everything in Smart Security", "Smart lock & garage control", "Smart thermostat integration", "Voice control (Alexa & Google)"],
  },
  {
    name: "Smart Home + Video",
    price: "$49.99",
    tag: "Total awareness",
    features: ["Everything in Smart Home", "Doorbell + outdoor + indoor cameras", "Continuous video recording", "Smart Deter lurker protection"],
  },
];

const STATS = [
  ["24/7", "Professional monitoring"],
  ["~10 sec", "Average alarm response"],
  ["2M+", "Homes protected"],
  ["4.6★", "Rated smart home app"],
];

export default function VivintPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", package: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/vivint/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again or call us at (470) 814-0464.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#060504]" data-testid="vivint-page">
      {/* Cinematic hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="vivint-hero">
        <img src="/vivint/hero.jpg" alt="Vivint protected smart home at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060504] via-[#060504]/55 to-[#060504]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060504]/80 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-20 pt-44">
          <Reveal>
            <img
              src="/vivint/vivint-logo.png"
              alt="Vivint"
              data-testid="vivint-logo"
              className="h-12 md:h-16 w-auto drop-shadow-[0_6px_35px_rgba(255,122,0,0.5)]"
            />
            <div className="mt-6 flex items-center gap-3">
              <span className="h-[3px] w-10" style={{ backgroundColor: ORANGE }} />
              <p className="font-body text-xs md:text-sm font-bold tracking-[0.3em] uppercase" style={{ color: ORANGE }}>
                Vivint Smart Home Security · Through Heritage
              </p>
            </div>
            <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] max-w-3xl">
              Every door. Every window.
              <br />
              <span style={{ color: ORANGE }}>Every moment — guarded.</span>
            </h1>
            <p className="mt-5 max-w-xl font-body font-light text-base md:text-lg text-white/70 leading-relaxed">
              Award-winning cameras, smart locks, and sensors backed by 24/7 professional monitoring — designed,
              installed, and looked after so your family never has to wonder.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#vivint-quote"
                data-testid="vivint-hero-quote-cta"
                className="btn-sweep inline-flex items-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold text-black"
                style={{ backgroundColor: ORANGE }}
              >
                Get My Free Quote <Zap size={15} />
              </a>
              <a
                href="#vivint-packages"
                data-testid="vivint-hero-packages-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-body text-sm font-semibold text-white hover:border-[#FF7A00] hover:text-[#FF7A00] transition-colors"
              >
                Explore Packages
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-y border-white/10" style={{ backgroundColor: ORANGE }} data-testid="vivint-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map(([v, l]) => (
            <div key={l}>
              <p className="font-serif-display text-3xl md:text-4xl font-bold text-black">{v}</p>
              <p className="mt-1 font-body text-xs md:text-sm font-semibold text-black/70">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Equipment */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-[3px] w-10" style={{ backgroundColor: ORANGE }} />
              <p className="font-body text-xs md:text-sm font-bold tracking-[0.3em] uppercase" style={{ color: ORANGE }}>The Equipment</p>
            </div>
            <h2 className="mt-4 font-serif-display text-3xl md:text-5xl font-bold text-white max-w-2xl">
              Hardware that watches, so you don't have to.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vivint-equipment-grid">
            {EQUIPMENT.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.04}>
                <div className="group h-full rounded-2xl border border-white/10 bg-[#0d0b09] p-7 transition-colors duration-300 hover:border-[#FF7A00]/60" data-testid={`vivint-equipment-${i}`}>
                  <e.icon size={28} strokeWidth={1.4} style={{ color: ORANGE }} />
                  <h3 className="mt-5 font-serif-display text-xl font-bold text-white">{e.name}</h3>
                  <p className="mt-2 font-body font-light text-sm text-white/55 leading-relaxed">{e.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* App section */}
      <section className="py-16 md:py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src="/vivint/app.jpg" alt="Vivint smart home app control" className="rounded-3xl border border-white/10 w-full object-cover" data-testid="vivint-app-image" />
          </Reveal>
          <Reveal delay={0.08}>
            <div className="flex items-center gap-3">
              <Smartphone size={18} style={{ color: ORANGE }} />
              <p className="font-body text-xs md:text-sm font-bold tracking-[0.3em] uppercase" style={{ color: ORANGE }}>One App Runs It All</p>
            </div>
            <h2 className="mt-4 font-serif-display text-3xl md:text-4xl font-bold text-white">
              Your whole home, in your pocket.
            </h2>
            <ul className="mt-7 space-y-4">
              {[
                "Arm, disarm, lock, and unlock from anywhere on earth",
                "Live camera feeds with two-way talk on every device",
                "Instant alerts the second a door opens or a person is detected",
                "Automations — lights on at sunset, doors locked at bedtime",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 font-body text-sm md:text-base text-white/70">
                  <Check size={17} className="mt-0.5 shrink-0" style={{ color: ORANGE }} /> {f}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Packages */}
      <section id="vivint-packages" className="py-20 md:py-28 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-[3px] w-10" style={{ backgroundColor: ORANGE }} />
              <p className="font-body text-xs md:text-sm font-bold tracking-[0.3em] uppercase" style={{ color: ORANGE }}>Monitoring Packages</p>
            </div>
            <h2 className="mt-4 font-serif-display text-3xl md:text-5xl font-bold text-white">Pick your level of protection.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="vivint-packages-grid">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.05}>
                <div
                  className={`h-full rounded-3xl border p-8 flex flex-col ${p.featured ? "bg-[#140c05]" : "bg-[#0d0b09] border-white/10"}`}
                  style={p.featured ? { borderColor: ORANGE } : {}}
                  data-testid={`vivint-package-${i}`}
                >
                  <p className="font-body text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: ORANGE }}>{p.tag}</p>
                  <h3 className="mt-3 font-serif-display text-2xl font-bold text-white">{p.name}</h3>
                  <p className="mt-3 font-serif-display text-4xl font-bold text-white">
                    {p.price}<span className="font-body text-sm font-normal text-white/50">/mo*</span>
                  </p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white/70">
                        <ShieldCheck size={15} className="mt-0.5 shrink-0" style={{ color: ORANGE }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#vivint-quote"
                    data-testid={`vivint-package-cta-${i}`}
                    className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 font-body text-sm font-semibold transition-colors ${p.featured ? "text-black" : "border border-white/25 text-white hover:border-[#FF7A00] hover:text-[#FF7A00]"}`}
                    style={p.featured ? { backgroundColor: ORANGE } : {}}
                  >
                    Quote This Package
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-body text-[11px] text-white/35">
            *Estimated monitoring pricing — equipment priced separately with flexible financing. Exact pricing confirmed on your custom quote.
          </p>
        </div>
      </section>

      {/* Quote form */}
      <section id="vivint-quote" className="py-20 md:py-28 border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {done ? (
            <div data-testid="vivint-quote-success" className="rounded-3xl border p-10 md:p-14 text-center" style={{ borderColor: `${ORANGE}66`, backgroundColor: "#0d0b09" }}>
              <CheckCircle2 size={48} className="mx-auto" style={{ color: ORANGE }} strokeWidth={1.2} />
              <h3 className="mt-6 font-serif-display text-3xl md:text-4xl text-white">Quote request received.</h3>
              <p className="mt-4 font-body font-light text-base md:text-lg text-white/60 max-w-xl mx-auto">
                Thank you! Our team will design your custom Vivint system and reach out with exact pricing and install options.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#0d0b09] p-8 md:p-10 space-y-5" data-testid="vivint-quote-form">
              <div>
                <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase font-bold" style={{ color: ORANGE }}>Free Custom Quote</p>
                <h2 className="mt-3 font-serif-display text-3xl md:text-4xl text-white">Protect your home this week.</h2>
                <p className="mt-3 font-body font-light text-sm md:text-base text-white/55">
                  Tell us about your home — we'll build your system, price it, and walk you through everything. No obligation.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="vivint-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="vivint-phone-input" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="vivint-email-input" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
                <input data-testid="vivint-address-input" className={inputCls} placeholder="City & state (or full address)" value={form.address} onChange={set("address")} />
              </div>
              <select data-testid="vivint-package-select" className={`${inputCls} ${form.package ? "text-white" : "text-white/30"}`} value={form.package} onChange={set("package")}>
                <option value="" disabled>Which package interests you?</option>
                {PACKAGES.map((p) => (
                  <option key={p.name} value={p.name} className="bg-[#0d0b09] text-white">{p.name} — {p.price}/mo</option>
                ))}
                <option value="Not sure yet" className="bg-[#0d0b09] text-white">Not sure yet — help me choose</option>
              </select>
              <textarea data-testid="vivint-message-input" rows={3} className={inputCls} placeholder="Anything else? Cameras you want, number of doors, current system... (optional)" value={form.message} onChange={set("message")} />
              <button
                type="submit"
                disabled={sending}
                data-testid="vivint-quote-submit"
                className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold text-black disabled:opacity-60"
                style={{ backgroundColor: ORANGE }}
              >
                {sending ? "Sending..." : "Request My Free Quote"} <Send size={16} />
              </button>
            </form>
          )}
        </div>
      </section>

      <div className="py-10 border-t border-white/10">
        <p className="max-w-5xl mx-auto px-6 md:px-12 font-body text-[11px] text-white/35 leading-relaxed">
          Vivint and the Vivint logo are trademarks of Vivint, Inc. Packages, pricing, and equipment availability are
          estimates and subject to confirmation on your custom quote. Heritage Family Protection Agency connects families
          with Vivint smart home security solutions.
        </p>
      </div>
    </div>
  );
}
